import { Col, Row } from "antd";
import cuid from "cuid";
import { get, isEmpty, set } from "lodash";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  MdOutlineBadge,
  MdOutlineBusiness,
  MdOutlineCallReceived,
  MdOutlineQrCode,
} from "react-icons/md";
import Form from "shared/components/Form";
import Input from "shared/components/Input";
import { NAME_SPACES } from "shared/locales/constants";
import {
  BANK_ACCOUNT_OWNER_TYPE,
  PAYMENT_RECEIVER,
  SUBSCRIPTION_FORM_KEYS,
} from "utils/constants";

const Refund = ({
  formProps,
  setDisabled,
  values,
  setPercent,
  errors,
  initialValues,
  isFieldRequired,
}) => {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.BROKER.CONTRACT);
  const SUBSCRIPTION = t("SUBSCRIPTION", { returnObjects: true });

  // Local state to track receiver type for re-rendering
  const [receiverType, setReceiverType] = useState(
    get(initialValues, "payment.receiver.owner.type") ||
      BANK_ACCOUNT_OWNER_TYPE.PARTICULAR,
  );
  const [isAnotherAccount, setIsAnotherAccount] = useState(
    get(initialValues, "isPayerReceiver") === PAYMENT_RECEIVER.ANOTHER_ACCOUNT,
  );

  const PAYMENT_RECEIVER_VALUES = {
    [PAYMENT_RECEIVER.SAME_ACCOUNT]: () =>
      set(
        initialValues,
        "payment.receiver",
        get(initialValues, "payment.payer"),
      ),
    [PAYMENT_RECEIVER.ANOTHER_ACCOUNT]: () =>
      set(initialValues, "payment.receiver", {
        id: cuid(),
        owner: { id: cuid(), type: BANK_ACCOUNT_OWNER_TYPE.PARTICULAR },
      }),
  };

  return (
    <div
      className="subscription-form--wrapper"
      id={`${SUBSCRIPTION_FORM_KEYS.ADDITIONAL_INFORMATION.REFUND}-section`}
    >
      <Form
        type="vertical"
        title={SUBSCRIPTION.FORM.ADDITIONAL_INFORMATION.REFUND.TITLE}
      >
        <Row gutter={[16, 16]}>
          <Col lg={24} xs={24}>
            <Input
              type="radio"
              variant="group"
              icon={<MdOutlineCallReceived size={16} />}
              label={
                SUBSCRIPTION.FORM.ADDITIONAL_INFORMATION.REFUND.ACCOUNT.LABEL
              }
              value={get(initialValues, "isPayerReceiver")}
              values={[
                {
                  value: PAYMENT_RECEIVER.SAME_ACCOUNT,
                  label:
                    SUBSCRIPTION.FORM.ADDITIONAL_INFORMATION.REFUND.ACCOUNT
                      .OPTIONS.USER_ACCOUNT,
                  popover:
                    !(
                      get(initialValues, "payment.payer.IBAN") &&
                      get(initialValues, "payment.payer.BIC")
                    ) &&
                    SUBSCRIPTION.FORM.ADDITIONAL_INFORMATION.REFUND.ACCOUNT
                      .POPOVER,
                },
                {
                  value: PAYMENT_RECEIVER.ANOTHER_ACCOUNT,
                  label:
                    SUBSCRIPTION.FORM.ADDITIONAL_INFORMATION.REFUND.ACCOUNT
                      .OPTIONS.OTHER,
                },
              ]}
              errors={errors["isPayerReceiver"]}
              touched={!isEmpty(errors["isPayerReceiver"])}
              onBlur={() => setPercent(initialValues)}
              disabled={
                !(
                  get(initialValues, "payment.payer.IBAN") &&
                  get(initialValues, "payment.payer.BIC")
                )
              }
              onChange={(value) => {
                set(initialValues, "isPayerReceiver", value);
                // Reset receiver type to PARTICULAR when switching
                if (value === PAYMENT_RECEIVER.ANOTHER_ACCOUNT) {
                  setReceiverType(BANK_ACCOUNT_OWNER_TYPE.PARTICULAR);
                }
                setIsAnotherAccount(value === PAYMENT_RECEIVER.ANOTHER_ACCOUNT);
                setDisabled((prev) => ({
                  ...prev,
                  proof: {
                    ...prev.proof,
                    refundAccount: value === PAYMENT_RECEIVER.SAME_ACCOUNT,
                  },
                }));
                PAYMENT_RECEIVER_VALUES[value]();
              }}
              required={isFieldRequired("isPayerReceiver")}
            />
          </Col>
          {isAnotherAccount && (
            <>
              <Col lg={24} xs={24}>
                <Input
                  type="switch"
                  icon={<MdOutlineBusiness size={16} />}
                  label={
                    SUBSCRIPTION.FORM.ADDITIONAL_INFORMATION.REFUND
                      .IS_ORGANIZATION
                  }
                  checked={receiverType === BANK_ACCOUNT_OWNER_TYPE.COMPANY}
                  onChange={(checked) => {
                    const newType = checked
                      ? BANK_ACCOUNT_OWNER_TYPE.COMPANY
                      : BANK_ACCOUNT_OWNER_TYPE.PARTICULAR;
                    // Update type in initialValues
                    set(initialValues, "payment.receiver.owner.type", newType);
                    // Reset all three fields when switching type
                    set(
                      initialValues,
                      "payment.receiver.owner.firstName",
                      null,
                    );
                    set(initialValues, "payment.receiver.owner.lastName", null);
                    set(
                      initialValues,
                      "payment.receiver.owner.socialReason",
                      null,
                    );
                    // Update local state to trigger re-render
                    setReceiverType(newType);
                    // Update disabled state for receiver owner fields
                    setDisabled((prev) => ({
                      ...prev,
                      payment: {
                        ...prev.payment,
                        receiver: {
                          ...prev.payment?.receiver,
                          owner: {
                            ...prev.payment?.receiver?.owner,
                            // If COMPANY, disable firstName/lastName; otherwise disable socialReason
                            socialReason: !checked,
                            firstName: checked,
                            lastName: checked,
                          },
                        },
                      },
                    }));
                    setPercent(initialValues);
                  }}
                />
              </Col>
              {receiverType === BANK_ACCOUNT_OWNER_TYPE.COMPANY ? (
                <Col key="receiver-org-col" lg={24} xs={24}>
                  <Input
                    key="receiver-socialReason"
                    label={
                      SUBSCRIPTION.FORM.ADDITIONAL_INFORMATION.PAYMENT.PAYER
                        .ORGANIZATION.SOCIAL_REASON
                    }
                    icon={<MdOutlineBadge size={16} />}
                    placeholder={
                      SUBSCRIPTION.FORM.ADDITIONAL_INFORMATION.PAYMENT.PAYER
                        .ORGANIZATION.SOCIAL_REASON
                    }
                    defaultValue={get(
                      initialValues,
                      "payment.receiver.owner.socialReason",
                    )}
                    errors={errors["payment.receiver.owner.socialReason"]}
                    touched={
                      !isEmpty(errors["payment.receiver.owner.socialReason"])
                    }
                    onChange={({ target: { value } }) => {
                      set(
                        initialValues,
                        "payment.receiver.owner.socialReason",
                        value,
                      );
                    }}
                    onBlur={() => setPercent(initialValues)}
                    required={isFieldRequired(
                      "payment.receiver.owner.socialReason",
                    )}
                  />
                </Col>
              ) : (
                <>
                  <Col key="receiver-lastName-col" lg={12} xs={12}>
                    <Input
                      key="receiver-lastName"
                      label={SUBSCRIPTION.FORM.OWNER.LASTNAME.LABEL}
                      icon={<MdOutlineBadge size={16} />}
                      placeholder={SUBSCRIPTION.FORM.OWNER.LASTNAME.PLACEHOLDER}
                      defaultValue={get(
                        initialValues,
                        "payment.receiver.owner.lastName",
                      )}
                      errors={errors["payment.receiver.owner.lastName"]}
                      touched={
                        !isEmpty(errors["payment.receiver.owner.lastName"])
                      }
                      onChange={({ target: { value } }) => {
                        set(
                          initialValues,
                          "payment.receiver.owner.lastName",
                          value,
                        );
                      }}
                      onBlur={() => setPercent(initialValues)}
                      required={isFieldRequired(
                        "payment.receiver.owner.lastName",
                      )}
                    />
                  </Col>
                  <Col key="receiver-firstName-col" lg={12} xs={12}>
                    <Input
                      key="receiver-firstName"
                      label={SUBSCRIPTION.FORM.OWNER.FIRSTNAME.LABEL}
                      icon={<MdOutlineBadge size={16} />}
                      placeholder={
                        SUBSCRIPTION.FORM.OWNER.FIRSTNAME.PLACEHOLDER
                      }
                      defaultValue={get(
                        initialValues,
                        "payment.receiver.owner.firstName",
                      )}
                      errors={errors["payment.receiver.owner.firstName"]}
                      touched={
                        !isEmpty(errors["payment.receiver.owner.firstName"])
                      }
                      onChange={({ target: { value } }) => {
                        set(
                          initialValues,
                          "payment.receiver.owner.firstName",
                          value,
                        );
                      }}
                      onBlur={() => setPercent(initialValues)}
                      required={isFieldRequired(
                        "payment.receiver.owner.firstName",
                      )}
                    />
                  </Col>
                </>
              )}
              <Col lg={24} xs={24}>
                <Input
                  label={SUBSCRIPTION.FORM.IBAN.LABEL}
                  icon={<MdOutlineQrCode size={16} />}
                  placeholder={SUBSCRIPTION.FORM.IBAN.PLACEHOLDER}
                  defaultValue={get(initialValues, "payment.receiver.IBAN")}
                  errors={errors["payment.receiver.IBAN"]}
                  touched={!isEmpty(errors["payment.receiver.IBAN"])}
                  onChange={({ target: { value } }) => {
                    set(initialValues, "payment.receiver.IBAN", value);
                  }}
                  onBlur={() => setPercent(initialValues)}
                  required={isFieldRequired("payment.receiver.IBAN")}
                />
              </Col>
              <Col lg={24} xs={24}>
                <Input
                  label={SUBSCRIPTION.FORM.BIC.LABEL}
                  icon={<MdOutlineQrCode size={16} />}
                  placeholder={SUBSCRIPTION.FORM.BIC.PLACEHOLDER}
                  defaultValue={get(initialValues, "payment.receiver.BIC")}
                  errors={errors["payment.receiver.BIC"]}
                  touched={!isEmpty(errors["payment.receiver.BIC"])}
                  onChange={({ target: { value } }) => {
                    set(initialValues, "payment.receiver.BIC", value);
                  }}
                  onBlur={() => setPercent(initialValues)}
                  required={isFieldRequired("payment.receiver.BIC")}
                />
              </Col>
            </>
          )}
        </Row>
      </Form>
    </div>
  );
};

export default Refund;
